import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography,
  useMediaQuery, useTheme, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, alpha,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useApp } from '../contexts/AppContext';
import NotificationDialog from './NotificationDialog';

const NAV_ITEMS = [
  { label: '首页', path: '/home', icon: <HomeRoundedIcon /> },
  { label: '自定义刷题', path: '/custom', icon: <TuneRoundedIcon /> },
  { label: '搜索', path: '/search', icon: <SearchRoundedIcon /> },
  { label: '刷题', path: '/quiz/active', icon: <PlayArrowRoundedIcon /> },
  { label: '设置', path: '/settings', icon: <SettingsRoundedIcon /> },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { state, getUnreadNotifications, markNotificationsAsRead } = useApp();
  const [showNotifications, setShowNotifications] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentPath = location.pathname;

  useEffect(() => {
    const unread = getUnreadNotifications();
    if (unread.length > 0) {
      setShowNotifications(true);
    }
  }, [getUnreadNotifications]);

  const handleCloseNotifications = () => {
    const unread = getUnreadNotifications();
    markNotificationsAsRead(unread.map(n => n.id));
    setShowNotifications(false);
  };

  // Respect user setting for nav on quiz page
  const isQuizPage = currentPath.startsWith('/quiz/active');
  const isResultPage = currentPath.startsWith('/result');
  const hideNav = isResultPage || (isQuizPage && !state.settings.showNavOnQuiz);

  // Desktop: side navigation
  if (!isMobile) {
    return (
      <>
        <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
          <Box
            sx={{
              width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
              borderRight: '1px solid', borderColor: 'divider',
              bgcolor: 'background.paper', py: 2, px: 1.5,
            }}
          >
            <Box sx={{ px: 2, pb: 2 }}>
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 800 }}>H</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>HodQuiz</Typography>
            </Box>
            <Divider />
            <List sx={{ mt: 1, flex: 1 }}>
              {NAV_ITEMS.map(item => {
                const isActive = currentPath.startsWith(item.path);
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => navigate(item.path)}
                      sx={{
                        borderRadius: 3, py: 1.2,
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.primary.main, 0.16),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.24) },
                          '& .MuiListItemIcon-root': { color: 'primary.main' },
                          '& .MuiListItemText-primary': { fontWeight: 600, color: 'primary.main' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box component="main" sx={{ flex: 1, height: '100dvh', overflow: 'auto', bgcolor: 'background.default' }}>
            <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 3, minHeight: '100%' }}>{children}</Box>
          </Box>
        </Box>
        <NotificationDialog
          open={showNotifications}
          notifications={getUnreadNotifications()}
          onClose={handleCloseNotifications}
        />
      </>
    );
  }

  // Mobile
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', bgcolor: 'background.default' }}>
        <Box component="main" sx={{ flex: 1, minHeight: 0, overflow: 'auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch', pb: hideNav ? 0 : '72px' }}>
          {children}
        </Box>
        {!hideNav && (
          <Box
            sx={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: 'blur(20px)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: 64, px: 1 }}>
              {NAV_ITEMS.map((item) => {
                const isActive = currentPath.startsWith(item.path);
                return (
                  <Box
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', gap: 0.5, px: 1.5, py: 0.75,
                      borderRadius: 4, cursor: 'pointer',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      transition: 'color 0.2s ease', userSelect: 'none',
                      '&:active': { transform: 'scale(0.96)' },
                    }}
                  >
                    <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: isActive ? 600 : 400, letterSpacing: '0.2px' }}>
                      {item.label}
                    </Typography>
                    {isActive && (
                      <Box sx={{ position: 'absolute', bottom: 2, width: 16, height: 3, borderRadius: 1.5, bgcolor: 'primary.main' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
      <NotificationDialog
        open={showNotifications}
        notifications={getUnreadNotifications()}
        onClose={handleCloseNotifications}
      />
    </>
  );
}
